import { Box, Edges } from '@react-three/drei'
import { ThreeEvent } from '@react-three/fiber'
import { formatForLabel } from 'features/siteMap/communalMapStyling/FormatForLabel'
import { useTilesetMapFeatureHelpers } from 'features/siteMap/tilesetMap/features/useTilesetMapFeatureHelpers'
import { TilesetMapFeatureLabel } from 'features/siteMap/tilesetMap/objects/TilesetMapFeatureLabel'
import {
  MapFeaturePattern,
  TilesetMapFeatureStandardMaterial,
} from 'features/siteMapViz'
import React, { ReactNode, useState } from 'react'
import { SiteFeaturePrimitiveBox } from 'types'

import { convertRotationToRadians } from './utils'

export const TilesetMapFeatureBox3D: React.FC<{
  box: SiteFeaturePrimitiveBox
  name: string | undefined
  color: string
  visible: boolean
  pattern: MapFeaturePattern
  onClick: (e: ThreeEvent<MouseEvent>) => void
  extraLabelDisplay?: ReactNode
}> = ({ box, color, visible, pattern, extraLabelDisplay, onClick, name }) => {
  const [showName, setShowName] = useState(false)

  const { convertCartographicToPosition, getFeatureOpacity } =
    useTilesetMapFeatureHelpers()

  const xyz = React.useMemo(
    () => convertCartographicToPosition(box.coordinate),
    [convertCartographicToPosition, box.coordinate]
  )
  const opacity = getFeatureOpacity(box)

  return (
    <group position={xyz} visible={visible}>
      <Box
        name={box.id}
        userData={{ siteFeature: box, uid: box.id }}
        onPointerMove={(e) => {
          setShowName(
            e.intersections.at(0)?.eventObject.userData.uid === box.id
          )
        }}
        onPointerLeave={() => setShowName(false)}
        args={[box.geometry.width, box.geometry.height, box.geometry.depth]}
        rotation={convertRotationToRadians(box.rotation)}
        onClick={onClick}>
        <TilesetMapFeatureStandardMaterial
          color={color}
          baseOpacity={opacity}
          pattern={pattern}
          importantFaceLength={Math.min(box.geometry.width, box.geometry.depth)}
        />
        <Edges
          color={'white'}
          lineWidth={0}
          transparent={true}
          opacity={opacity}
          polygonOffset={true}
          polygonOffsetFactor={-7}
          polygonOffsetUnits={-7}
        />
      </Box>
      {!name ? null : (
        <TilesetMapFeatureLabel
          body={formatForLabel(name, extraLabelDisplay)}
          hovered={showName}
          visible={visible}
        />
      )}
    </group>
  )
}
